/** Inbox module: sidebar + conversation list. */
"use client";

import { Circle, CircleCheck, Clock } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { MODULE_HEIGHT, SIDEBAR_WIDTH } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";
import { useInboxUi } from "./inbox-ui-context";
import {
  InboxConversationRow,
  filterInboxConversations,
  getInboxSelectionTitle,
} from "./module-inbox-data";
import {
  InboxListFilterActions,
  InboxListHeaderIcon,
  StatusSegment,
} from "./module-inbox-list-chrome";
import { ModuleInboxSidebar } from "./module-inbox-sidebar";
import type { HeroVorgangConversationId } from "./module-inbox-vorgang-data";
import { ModuleInboxVorgangDetail } from "./module-inbox-vorgang-detail";

export function ModuleInbox(_props: ModuleProps): ReactNode {
  const tModuleInboxData = useTranslations("marketing.moduleInboxData");
  const t = useTranslations("marketing.moduleInbox");
  const [selectedId, setSelectedId] =
    useState<HeroVorgangConversationId | null>(null);
  const [showClickPulse, setShowClickPulse] = useState<boolean>(true);
  const {
    inboxSelection: selection,
    setInboxSelection: setSelection,
    setInVorgangDetail,
  } = useInboxUi();

  const visibleConversations = filterInboxConversations(
    tModuleInboxData,
    selection,
  );
  const listTitle = getInboxSelectionTitle(tModuleInboxData, selection);

  const dismissClickPulse = useCallback((): void => {
    setShowClickPulse(false);
  }, []);

  const openConversation = useCallback(
    (conversationId: HeroVorgangConversationId): void => {
      dismissClickPulse();
      setSelectedId(conversationId);
    },
    [dismissClickPulse],
  );

  useEffect((): (() => void) => {
    setInVorgangDetail(selectedId !== null);
    return (): void => {
      setInVorgangDetail(false);
    };
  }, [selectedId, setInVorgangDetail]);

  if (selectedId !== null) {
    return (
      <div
        style={{
          display: "flex",
          width: "100%",
          height: MODULE_HEIGHT,
        }}
      >
        <ModuleInboxVorgangDetail
          conversationId={selectedId}
          onBack={() => {
            setSelectedId(null);
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", height: MODULE_HEIGHT }}>
      <div
        style={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          borderRight: "1px solid #EEF0F3",
          background: "#FBFBFC",
          padding: "16px 12px",
          boxSizing: "border-box",
          height: MODULE_HEIGHT,
          overflowY: "auto",
          overflowX: "hidden",
          position: "relative",
          zIndex: 2,
        }}
      >
        <ModuleInboxSidebar
          selection={selection}
          onSelectionChange={setSelection}
        />
      </div>

      <div
        style={{
          flex: 1,
          minWidth: 0,
          background: "#fff",
          padding: "20px 22px",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "relative" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 9,
                fontSize: 18,
                fontWeight: 700,
              }}
            >
              <InboxListHeaderIcon selection={selection} />
              {listTitle}
            </span>
            <InboxListFilterActions />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "#F4F5F7",
              borderRadius: 10,
              padding: 3,
              width: "fit-content",
              whiteSpace: "nowrap",
              marginBottom: 8,
            }}
          >
            <StatusSegment
              active
              icon={
                <span
                  style={{
                    color: "rgba(11,15,25,0.5)",
                    display: "inline-flex",
                  }}
                >
                  <Circle width={14} height={14} strokeWidth={2} />
                </span>
              }
              label={t("toDo")}
            />
            <StatusSegment
              icon={
                <span style={{ color: "#E0A500", display: "inline-flex" }}>
                  <Clock width={14} height={14} strokeWidth={2} />
                </span>
              }
              label={t("wiedervorlage")}
            />
            <StatusSegment
              icon={
                <span style={{ color: "#3A9E6B", display: "inline-flex" }}>
                  <CircleCheck width={14} height={14} strokeWidth={2} />
                </span>
              }
              label={t("erledigt")}
            />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 6px",
              borderBottom: "1px solid #F1F2F4",
            }}
          >
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: 5,
                border: "1.5px solid #D7DAE0",
                boxSizing: "border-box",
              }}
            />
          </div>

          {visibleConversations.map((c, index) => (
            <InboxConversationRow
              key={c.id}
              conversation={c}
              showClickPulse={showClickPulse && index === 0}
              onClick={() => {
                openConversation(c.id);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
